import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Archive, Play, Copy, AlertTriangle, Edit3, Trash2 } from "lucide-react";

const CATEGORY_TYPES = [
  { value: "headliner", label: "Headliner" },
  { value: "international", label: "International" },
  { value: "danish", label: "Dansk" },
  { value: "bonus_orange", label: "Bonus (Orange åbner)" },
];

export default function SeasonPanel() {
  const qc = useQueryClient();
  const { data: seasons } = useQuery({ queryKey: ["admin-seasons"], queryFn: () => api.get("/admin/seasons").then(r => r.data) });
  const active = seasons?.find(s => s.status === "active");
  const archived = seasons?.filter(s => s.status !== "active") || [];
  const [editing, setEditing] = useState(null);
  const [starting, setStarting] = useState(false);

  const endSeason = async (season_id) => {
    if (!window.confirm(`Er du sikker på at du vil afslutte sæsonen? Alle scores låses.`)) return;
    if (!window.confirm(`Sidste bekræftelse – dette kan ikke fortrydes.`)) return;
    try {
      await api.post(`/admin/seasons/${season_id}/end`);
      toast.success("Sæson afsluttet og arkiveret");
      qc.invalidateQueries();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fejl"); }
  };

  const resetBadges = async () => {
    if (!window.confirm("Er du sikker på, at du vil nulstille alle badges for alle brugere? Denne handling kan ikke fortrydes.")) return;
    if (!window.confirm("Sidste bekræftelse – alle brugeres badges/achievements slettes.")) return;
    try {
      const { data } = await api.post("/admin/achievements/reset-all");
      toast.success(`Nulstillet – ${data.deleted} badges slettet`);
      qc.invalidateQueries();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fejl"); }
  };

  return (
    <div className="space-y-8" data-testid="season-panel">
      {active && (
        <div className="card-flat p-6" data-testid="season-active-card">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="label-eyebrow text-emerald-400">Aktiv sæson</div>
              <h3 className="font-display font-bold text-3xl mt-2">{active.name}</h3>
              <div className="text-sm text-zinc-400 mt-2 flex items-center gap-3">
                <span className="font-score text-lg text-white">{active.short_name}</span>
                <span>·</span>
                <span>Deadline: {new Date(active.deadline).toLocaleString("da-DK")}</span>
                <span>·</span>
                <span>{active.festivals?.length ?? 0} festivaler</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditing(active)} data-testid="season-edit-btn"><Edit3 size={14} className="mr-1" /> Rediger</Button>
              <Button variant="outline" className="text-red-400 border-red-400/40 hover:bg-red-500/10" onClick={() => endSeason(active.season_id)} data-testid="season-end-btn">
                <Archive size={14} className="mr-1" /> Afslut sæson
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h3 className="font-display font-bold text-xl">Sæson-arkiv</h3>
        <Button className="bg-white text-black hover:bg-zinc-200" onClick={() => setStarting(true)} data-testid="season-new-btn">
          <Plus size={14} className="mr-1" /> Start ny sæson
        </Button>
      </div>

      <div className="grid gap-3">
        {archived.map(s => (
          <div key={s.season_id} className="card-flat p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`season-archived-${s.season_id}`}>
            <div>
              <div className="font-display font-bold text-xl">{s.name}</div>
              <div className="text-sm text-zinc-500 mt-1">
                <span className="font-score text-white">{s.short_name}</span>
                {s.winner && (<span> · Vinder: <span className="text-yellow-400">🏆 {s.winner.display_name} ({s.winner.score})</span></span>)}
                {s.archived_at && (<span> · Arkiveret {new Date(s.archived_at).toLocaleDateString("da-DK")}</span>)}
              </div>
            </div>
            <span className="text-xs px-3 py-1 rounded-full bg-white/10 text-zinc-300">ARKIVERET</span>
          </div>
        ))}
        {archived.length === 0 && <div className="card-flat p-6 text-zinc-500 text-sm text-center">Ingen tidligere sæsoner endnu.</div>}
      </div>

      {editing && <SeasonEditDialog season={editing} onClose={() => setEditing(null)} onSaved={() => { qc.invalidateQueries(); setEditing(null); }} />}
      {starting && <NewSeasonDialog seasons={seasons} onClose={() => setStarting(false)} onCreated={() => { qc.invalidateQueries(); setStarting(false); }} />}

      {/* Danger zone */}
      <div className="card-flat p-6 border border-red-500/20" data-testid="season-danger-zone">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={16} className="text-red-400" />
          <h3 className="font-display font-bold text-lg text-red-300">Administrativ handling</h3>
        </div>
        <p className="text-sm text-zinc-400 mb-4">
          Nulstil alle badges/achievements på tværs af alle brugere. Bruges typisk før en ny sæson starter for at rydde badges fra testperioden. Brugerkonti, point, gæt og ligaer påvirkes ikke.
        </p>
        <Button
          variant="outline"
          className="text-red-400 border-red-400/40 hover:bg-red-500/10"
          onClick={resetBadges}
          data-testid="season-reset-badges-btn"
        >
          <Trash2 size={14} className="mr-1" /> Nulstil alle badges for alle brugere
        </Button>
      </div>
    </div>
  );
}

function NewSeasonDialog({ seasons, onClose, onCreated }) {
  const active = seasons?.find(s => s.status === "active");
  const [name, setName] = useState("");
  const [shortName, setShortName] = useState("");
  const [deadline, setDeadline] = useState("");
  const [copyFrom, setCopyFrom] = useState(active?.season_id || "");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!name || !shortName || !deadline) return toast.error("Udfyld alle felter");
    if (active) {
      if (!window.confirm(`Dette arkiverer "${active.name}" permanent og starter en ny sæson. Fortsæt?`)) return;
    }
    setBusy(true);
    try {
      await api.post("/admin/seasons", {
        name, short_name: shortName,
        deadline: new Date(deadline).toISOString(),
        copy_from: copyFrom || undefined,
      });
      toast.success("Ny sæson startet");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fejl"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Start ny sæson</DialogTitle>
          <DialogDescription>Den aktive sæson bliver arkiveret. Data bevares.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="label-eyebrow">Fuldt navn</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Festivalmanager 2028" data-testid="season-new-name" />
          </div>
          <div>
            <Label className="label-eyebrow">Kort navn</Label>
            <Input value={shortName} onChange={e => setShortName(e.target.value.toUpperCase())} placeholder="FM28" maxLength={10} data-testid="season-new-short" />
          </div>
          <div>
            <Label className="label-eyebrow">Deadline</Label>
            <Input type="datetime-local" value={deadline} onChange={e => setDeadline(e.target.value)} data-testid="season-new-deadline" />
          </div>
          <div>
            <Label className="label-eyebrow">Startopsætning</Label>
            <div className="mt-2 grid gap-2">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="radio" checked={copyFrom !== ""} onChange={() => setCopyFrom(active?.season_id || "")} />
                <Copy size={12} /> Kopier struktur fra {active?.name || "tidligere sæson"}
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="radio" checked={copyFrom === ""} onChange={() => setCopyFrom("")} />
                <Play size={12} /> Start fra bunden (standard 5 festivaler)
              </label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuller</Button>
          <Button className="bg-white text-black hover:bg-zinc-200" onClick={create} disabled={busy} data-testid="season-new-confirm">Opret sæson</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SeasonEditDialog({ season, onClose, onSaved }) {
  const [name, setName] = useState(season.name);
  const [shortName, setShortName] = useState(season.short_name);
  const [deadline, setDeadline] = useState(new Date(season.deadline).toISOString().slice(0, 16));
  const [festivals, setFestivals] = useState(season.festivals || []);
  const [busy, setBusy] = useState(false);

  const updateFestival = (idx, changes) => setFestivals(fs => fs.map((f, i) => i === idx ? { ...f, ...changes } : f));
  const updateCategory = (fidx, cidx, changes) =>
    setFestivals(fs => fs.map((f, i) => i !== fidx ? f : {
      ...f, categories: f.categories.map((c, j) => j === cidx ? { ...c, ...changes } : c)
    }));
  const addCategory = (fidx) => setFestivals(fs => fs.map((f, i) => i !== fidx ? f : {
    ...f, categories: [...(f.categories || []), { category_id: `cat_${Date.now()}`, name: "Ny kategori", type: "international", count: 1, points: 4, counts_toward_total: true, is_bonus: false }]
  }));
  const removeCategory = (fidx, cidx) => {
    if (!window.confirm("Slet denne kategori?")) return;
    setFestivals(fs => fs.map((f, i) => i !== fidx ? f : { ...f, categories: f.categories.filter((_, j) => j !== cidx) }));
  };
  const removeFestival = (fidx) => {
    if (!window.confirm("Fjern denne festival fra sæsonen?")) return;
    setFestivals(fs => fs.filter((_, i) => i !== fidx));
  };
  const addFestival = () => setFestivals(fs => [...fs, {
    festival_id: `fest_${Date.now()}`, name: "Ny festival", color: "#FFFFFF",
    order: fs.length + 1, active: true, categories: [
      { category_id: "headliner", name: "Headliner", type: "headliner", count: 3, points: 12, counts_toward_total: true, is_bonus: false },
      { category_id: "international", name: "International", type: "international", count: 5, points: 4, counts_toward_total: true, is_bonus: false },
      { category_id: "danish", name: "Dansk", type: "danish", count: 5, points: 1, counts_toward_total: true, is_bonus: false },
    ]
  }]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/seasons/${season.season_id}`, {
        name, short_name: shortName,
        deadline: new Date(deadline).toISOString(),
        festivals,
      });
      toast.success("Sæson gemt");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fejl"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Rediger sæson</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label className="label-eyebrow">Navn</Label><Input value={name} onChange={e => setName(e.target.value)} /></div>
            <div><Label className="label-eyebrow">Kort navn</Label><Input value={shortName} onChange={e => setShortName(e.target.value.toUpperCase())} /></div>
            <div><Label className="label-eyebrow">Deadline</Label><Input type="datetime-local" value={deadline} onChange={e => setDeadline(e.target.value)} /></div>
          </div>

          <div className="border-t border-white/5 pt-4 flex justify-between items-center">
            <h4 className="font-display font-bold text-lg">Festivaler & kategorier</h4>
            <Button variant="outline" size="sm" onClick={addFestival}><Plus size={12} className="mr-1" /> Tilføj festival</Button>
          </div>

          <div className="space-y-3">
            {festivals.map((f, fidx) => (
              <div key={f.festival_id} className="card-flat p-4" style={{ borderLeft: `3px solid ${f.color}` }} data-testid={`season-fest-${f.festival_id}`}>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
                  <div className="sm:col-span-2">
                    <Label className="label-eyebrow">Navn</Label>
                    <Input value={f.name} onChange={e => updateFestival(fidx, { name: e.target.value })} />
                  </div>
                  <div>
                    <Label className="label-eyebrow">Farve</Label>
                    <Input type="color" value={f.color} onChange={e => updateFestival(fidx, { color: e.target.value })} className="h-10" />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={!!f.active} onCheckedChange={(v) => updateFestival(fidx, { active: !!v })} /> Aktiv
                    </label>
                    <button className="text-red-400 hover:text-red-300 ml-auto" onClick={() => removeFestival(fidx)}><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  {(f.categories || []).map((c, cidx) => (
                    <div key={cidx} className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end p-2 rounded bg-white/[0.03]">
                      <div><Label className="text-xs text-zinc-500">Navn</Label><Input value={c.name} onChange={e => updateCategory(fidx, cidx, { name: e.target.value })} /></div>
                      <div>
                        <Label className="text-xs text-zinc-500">Type</Label>
                        <select value={c.type} onChange={e => updateCategory(fidx, cidx, { type: e.target.value })}
                          className="w-full bg-black/60 border border-white/10 rounded-md px-2 py-2 text-sm">
                          {CATEGORY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      <div><Label className="text-xs text-zinc-500">Antal</Label><Input type="number" min={0} max={50} value={c.count} onChange={e => updateCategory(fidx, cidx, { count: parseInt(e.target.value || "0", 10) })} data-testid={`season-cat-count-${f.festival_id}-${cidx}`} /></div>
                      <div><Label className="text-xs text-zinc-500">Point</Label><Input type="number" min={0} max={100} value={c.points} onChange={e => updateCategory(fidx, cidx, { points: parseInt(e.target.value || "0", 10) })} data-testid={`season-cat-points-${f.festival_id}-${cidx}`} /></div>
                      <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <Checkbox checked={!!c.is_bonus} onCheckedChange={(v) => updateCategory(fidx, cidx, { is_bonus: !!v })} /> Bonus
                      </label>
                      <button className="text-zinc-500 hover:text-red-400 justify-self-end" onClick={() => removeCategory(fidx, cidx)}><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => addCategory(fidx)}><Plus size={12} className="mr-1" /> Tilføj kategori</Button>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-yellow-400/30 bg-yellow-500/5 p-3 text-xs text-yellow-200 flex gap-2 items-start">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <div>Ændringer i antal slots og pointværdier gælder alle nuværende og fremtidige gæt i denne sæson. Scoren opdateres automatisk.</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuller</Button>
          <Button className="bg-white text-black hover:bg-zinc-200" onClick={save} disabled={busy} data-testid="season-save-btn">Gem sæson</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
